/**
 * The minimum a node must provide to be navigated by these helpers, so the
 * catalogue tree and the template tree can share them despite carrying
 * different fields.
 */
export type TreeLike<T> = {
  id: string;
  children: T[];
};

export type CategoryStatus = "Enabled" | "Disabled" | "Disabled by parent";

/** UI status: a disabled ancestor takes precedence over the node's own setting. */
export function categoryStatuses<T extends TreeLike<T> & { isCanonical?: boolean }>(
  roots: T[],
): Map<string, CategoryStatus> {
  const statuses = new Map<string, CategoryStatus>();
  const walk = (nodes: T[], parentEnabled: boolean) => {
    for (const node of nodes) {
      let status: CategoryStatus = node.isCanonical === false ? "Disabled" : "Enabled";
      if (!parentEnabled) status = "Disabled by parent";
      statuses.set(node.id, status);
      walk(node.children, status === "Enabled");
    }
  };
  walk(roots, true);
  return statuses;
}

export interface FlatRow<T> {
  node: T;
  depth: number;
  /** 1-based index among siblings, for aria-posinset. */
  pos: number;
  /** Sibling count, for aria-setsize. */
  size: number;
}

/** Rows the user can currently see, depth-first, respecting expansion. */
export function flattenVisible<T extends TreeLike<T>>(
  roots: T[],
  expanded: ReadonlySet<string>,
): FlatRow<T>[] {
  const out: FlatRow<T>[] = [];
  const walk = (list: T[], depth: number) => {
    list.forEach((node, i) => {
      out.push({ node, depth, pos: i + 1, size: list.length });
      if (expanded.has(node.id) && node.children.length) {
        walk(node.children, depth + 1);
      }
    });
  };
  walk(roots, 0);
  return out;
}

export function descendantCount<T extends TreeLike<T>>(node: T): number {
  return node.children.reduce((sum, c) => sum + 1 + descendantCount(c), 0);
}

/** Ids to expand so an entire branch is open — only nodes that have children. */
export function collectBranchIds<T extends TreeLike<T>>(node: T): string[] {
  if (!node.children.length) return [];
  return node.children.reduce<string[]>(
    (acc, c) => acc.concat(collectBranchIds(c)),
    [node.id],
  );
}

/** Every descendant id, leaves included, excluding the node itself. */
export function descendantIds<T extends TreeLike<T>>(node: T): string[] {
  return node.children.reduce<string[]>(
    (acc, child) => acc.concat(child.id, descendantIds(child)),
    [],
  );
}

/** Ancestors of `id`, outermost first. Empty for roots and unknown ids. */
export function ancestorsOf<T extends TreeLike<T>>(roots: T[], id: string): T[] {
  const path: T[] = [];
  const walk = (list: T[]): boolean =>
    list.some((node) => {
      if (node.id === id) return true;
      path.push(node);
      if (walk(node.children)) return true;
      path.pop();
      return false;
    });
  return walk(roots) ? path : [];
}

export function findNode<T extends TreeLike<T>>(roots: T[], id: string): T | null {
  for (const node of roots) {
    if (node.id === id) return node;
    const found = findNode(node.children, id);
    if (found) return found;
  }
  return null;
}

export function countNodes<T extends TreeLike<T>>(roots: T[]): number {
  return roots.reduce((sum, n) => sum + 1 + countNodes(n.children), 0);
}
